import { GTM_ANALYTICS_CONSTANTS, SUBSCRIPTION_TYPE } from '../../constants';
import { DataTarget } from './types';

type FlowType = {
  value: SUBSCRIPTION_TYPE;
  isAny?: boolean;
};

type ThemeType = {
  value: string;
  isAny?: boolean;
};

type DataTargetType = {
  value: DataTarget;
  isAny?: boolean;
}

export enum EventType {
  PARTNER = 'partner',
  QUOTE = 'quote',
  SEGMENT = 'segment',
  REST = 'rest',
}

type EventCategoryType = {
  value: EventType;
  isAny?: boolean;
};

class Rule {
  flows?: FlowType[];
  themes?: ThemeType[];
  dataTargets?: DataTargetType[];
  eventCategories?: EventCategoryType[];
}

export class RuleHouse {
  rules: Rule[] = [];
  currentRule: Rule = new Rule();

  withAnyFlow = () => {
    this.currentRule.flows = [{ isAny: true, value: SUBSCRIPTION_TYPE.SUBSCRIBE }];
    return this;
  };

  withFlows = (...flows: SUBSCRIPTION_TYPE[]): RuleHouse => {
    this.currentRule.flows = flows.map(flow => ({ value: flow }));
    return this;
  };

  withThemes = (...themes: string[]): RuleHouse => {
    this.currentRule.themes = themes.map(theme => ({ value: theme }));
    return this;
  };

  withAnyDataTarget = () => {
    this.currentRule.dataTargets = [{ isAny: true, value: DataTarget.DEFAULT_DATA_LAYER }];
    return this;
  };

  withDataTargets = (...dataTargets: DataTarget[]): RuleHouse => {
    this.currentRule.dataTargets = dataTargets.map(target => ({ value: target }));
    return this;
  };

  withEventTypes = (...eventTypes: EventType[]): RuleHouse => {
    this.currentRule.eventCategories = eventTypes.map(eventType => ({ value: eventType }));
    return this;
  };

  allow = () => {
    this.rules.push(this.currentRule);
    this.currentRule = new Rule();
    return this;
  };

  validateOverlapping = (): void => {
    if (this.isOverlapping()) {
      throw new TypeError('Overlapping rules detected in RuleHouse');
    }
  }

  isOverlapping = (): boolean => {
    return this.rules.some((ruleA, indexA) =>
      this.rules.some((ruleB, indexB) => {
        if (indexA >= indexB) return false;

        const flowsOverlap =
          ruleA.flows &&
          ruleB.flows &&
          ruleA.flows.some(flowA =>
            ruleB.flows!.some(
              flowB =>
                flowA.isAny || flowB.isAny || flowA.value === flowB.value
            )
          );

        const themesOverlap =
          ruleA.themes &&
          ruleB.themes &&
          ruleA.themes.some(themeA =>
            ruleB.themes!.some(
              themeB =>
                themeA.isAny || themeB.isAny || themeA.value === themeB.value
            )
          );

        const eventCategoryOverlap =
          ruleA.eventCategories &&
          ruleB.eventCategories &&
          ruleA.eventCategories.some(eventCategoryA =>
            ruleB.eventCategories!.some(
              eventCategoryB =>
                eventCategoryA.isAny ||
                eventCategoryB.isAny ||
                eventCategoryA.value === eventCategoryB.value
            )
          );

        return flowsOverlap && themesOverlap && eventCategoryOverlap;
      })
    );
  };

  private getEventType = (eventName?: string): EventType => {
    if (eventName?.startsWith('partner_')) {
      return EventType.PARTNER;
    }
    if (eventName?.startsWith('quote_accept_')) {
      return EventType.QUOTE;
    }
    if (eventName && [GTM_ANALYTICS_CONSTANTS.ACCOUNT_SETUP_SEGMENT,
      GTM_ANALYTICS_CONSTANTS.BEGIN_CHECKOUT_SEGMENT,
      GTM_ANALYTICS_CONSTANTS.PURCHASE_SEGMENT,
      GTM_ANALYTICS_CONSTANTS.ADD_PAYMENT_INFO_SEGMENT,
      GTM_ANALYTICS_CONSTANTS.ENHANCED_PURCHASE
    ].includes(eventName)) {
      return EventType.SEGMENT;
    }

    return EventType.REST;
  };

  isAllowed = (
    flow: SUBSCRIPTION_TYPE,
    theme: string,
    eventName?: string,
    dataTarget?: DataTarget
  ): boolean => {
    const eventCategory = this.getEventType(eventName);

    return this.rules.some((rule) => {
      const {
        flows: ruleFlows,
        eventCategories: ruleEventCategories,
        themes: ruleThemes,
        dataTargets: ruleDataTargets
      } = rule;

      if (!ruleFlows || !ruleThemes || !ruleEventCategories) {
        return false;
      }
      const flowMatch = ruleFlows.some(ruleFlow => ruleFlow.isAny || ruleFlow.value === flow);
      const themeMatch = ruleThemes.some(ruleTheme => ruleTheme.isAny || ruleTheme.value === theme);
      const eventTypeMatch = ruleEventCategories.some(
        ruleEventCategory => ruleEventCategory.isAny || ruleEventCategory.value === eventCategory
      );
      const dataTargetMatch = !dataTarget || !ruleDataTargets || ruleDataTargets.some(
        ruleTarget => ruleTarget.isAny || ruleTarget.value === dataTarget
      );

      return flowMatch && themeMatch && eventTypeMatch && dataTargetMatch;
    });
  };
}
